import React, { useEffect, useRef, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { mediaPreviewUrl, mediaPublicUrl } from "@/lib/mediaUrl";
import PageHeader from "@/components/common/PageHeader";
import GlassCard from "@/components/common/GlassCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { MEDIA } from "@/constants/testIds";

function MediaThumb({ item, className }) {
  const [src, setSrc] = useState(() => mediaPreviewUrl(item));
  const [broken, setBroken] = useState(false);

  useEffect(() => {
    setBroken(false);
    setSrc(mediaPreviewUrl(item));
  }, [item?.id, item?.external_url, item?.updated_at]);

  if (!item?.id) return <div className={`${className} bg-white/5`} />;

  if (item.type === "image") {
    if (broken) {
      return (
        <div className={`${className} flex flex-col items-center justify-center gap-1 bg-black/50 text-amber-200/90`}>
          <Icons.ImageOff className="w-8 h-8" />
          <span className="text-[10px] uppercase tracking-wider">Missing file</span>
        </div>
      );
    }
    return (
      <img
        src={src}
        alt={item.alt_text || item.original_filename || "Media"}
        className={className}
        loading="lazy"
        onError={() => setBroken(true)}
      />
    );
  }
  if (item.type === "video") {
    return broken ? (
      <div className={`${className} flex items-center justify-center bg-black/50 text-amber-200`}><Icons.VideoOff className="w-8 h-8" /></div>
    ) : (
      <video src={src} className={className} controls preload="metadata" aria-label={item.alt_text || item.original_filename} onError={() => setBroken(true)} />
    );
  }
  if (item.content_type === "application/pdf") {
    return (
      <object data={src} type="application/pdf" className={className} aria-label={`Preview ${item.original_filename}`}>
        <Icons.FileText className="w-10 h-10 text-white/40" />
      </object>
    );
  }
  return (
    <div className={`${className} flex flex-col items-center justify-center text-white/50`}>
      <Icons.FileText className="w-10 h-10" />
      <span className="mt-2 text-[10px] uppercase">Document</span>
    </div>
  );
}

const bytes = (n) => {
  if (!n) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

export default function Media() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [folder, setFolder] = useState("all");
  const [folders, setFolders] = useState([]);
  const [selected, setSelected] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadQueue, setUploadQueue] = useState([]);
  const [editing, setEditing] = useState(null);
  const [metadata, setMetadata] = useState({});
  const [savingMetadata, setSavingMetadata] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [newFolder, setNewFolder] = useState("");
  const [storageInfo, setStorageInfo] = useState(null);
  const [bootstrapping, setBootstrapping] = useState(false);
  const [importUrl, setImportUrl] = useState("");
  const [replacingId, setReplacingId] = useState(null);
  const fileRef = useRef(null);
  const replaceRef = useRef(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { folder: folder || "all", page_size: 120 };
      if (q) params.q = q;
      if (type) params.type = type;
      const [{ data }, folderResponse, statusRes] = await Promise.all([
        api.get("/media", { params }),
        api.get("/media/folders"),
        api.get("/media/storage-status").catch(() => ({ data: null })),
      ]);
      setItems(data.items || []);
      setFolders(folderResponse.data.folders || []);
      setStorageInfo(statusRes.data || null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(fetchData, 250);
    return () => clearTimeout(t);
  }, [q, type, folder]); // eslint-disable-line

  const upload = async (fileList) => {
    if (!fileList?.length) return;
    setUploading(true);
    const queue = fileList.map((file) => ({ id: `${file.name}-${file.lastModified}`, name: file.name, progress: 0, status: "uploading" }));
    setUploadQueue(queue);
    try {
      for (const file of fileList) {
        const id = `${file.name}-${file.lastModified}`;
        const form = new FormData();
        form.append("file", file);
        form.append("folder", folder === "all" ? "uploads" : folder);
        await api.post("/media/upload", form, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (event) => {
            const progress = event.total ? Math.round((event.loaded / event.total) * 100) : 0;
            setUploadQueue((current) => current.map((item) => (item.id === id ? { ...item, progress } : item)));
          },
        });
        setUploadQueue((current) => current.map((item) => (item.id === id ? { ...item, progress: 100, status: "complete" } : item)));
      }
      toast.success(`Uploaded ${fileList.length} file(s)`);
      if (storageInfo && !storageInfo.durable) {
        toast.message("Storage is not durable", {
          description: "Configure Cloudflare R2 on the API so uploads survive restarts.",
        });
      }
      fetchData();
    } catch (e) {
      setUploadQueue((current) => current.map((item) => (item.status === "uploading" ? { ...item, status: "failed" } : item)));
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const replaceFile = async (file) => {
    if (!replacingId || !file) return;
    try {
      await api.post(`/media/${replacingId}/replace`, (() => {
        const form = new FormData();
        form.append("file", file);
        return form;
      })(), { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("File replaced — existing links keep the same media id");
      setReplacingId(null);
      fetchData();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Replace failed");
    }
  };

  const bootstrapWebsiteAssets = async () => {
    setBootstrapping(true);
    try {
      const { data } = await api.post("/media/bootstrap-website-assets");
      toast.success(`Imported ${data.created} website images (${data.skipped} already present)`);
      setFolder("services");
      fetchData();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not import website assets");
    } finally {
      setBootstrapping(false);
    }
  };

  const importExternalUrl = async () => {
    const url = importUrl.trim();
    if (!url) return;
    try {
      await api.post("/media/import-url", {
        url,
        folder: folder === "all" ? "uploads" : folder,
        filename: url.split("/").pop() || "imported-image",
      });
      toast.success("URL added to library");
      setImportUrl("");
      fetchData();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Import failed");
    }
  };

  const remove = async () => {
    const targets = pendingDelete === "bulk"
      ? selected
      : pendingDelete?.id ? [pendingDelete.id] : [];
    if (!targets.length) return;
    try {
      await Promise.all(targets.map((id) => api.delete(`/media/${id}`)));
      toast.success(targets.length > 1 ? `${targets.length} files deleted` : "Deleted");
      setSelected([]);
      setPendingDelete(null);
      fetchData();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not delete");
    }
  };

  const openMetadata = (item) => {
    setEditing(item);
    setMetadata({
      original_filename: item.original_filename || "",
      alt_text: item.alt_text || "",
      caption: item.caption || "",
      tags: item.tags || [],
      folder: item.folder || "uploads",
      external_url: item.external_url || "",
    });
  };

  const saveMetadata = async () => {
    if (!editing) return;
    setSavingMetadata(true);
    try {
      await api.patch(`/media/${editing.id}`, metadata);
      toast.success("Media details saved");
      setEditing(null);
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail) || "Could not save media details");
    } finally {
      setSavingMetadata(false);
    }
  };

  const toggleSelected = (id) => setSelected((current) => (current.includes(id) ? current.filter((item) => item !== id) : [...current, id]));

  const copyLink = async (item) => {
    const link = mediaPublicUrl(item);
    try {
      await navigator.clipboard.writeText(link);
      toast.success("File link copied");
    } catch {
      toast.error("Could not copy the file link");
    }
  };

  const createFolder = () => {
    const name = newFolder.trim().replace(/[^\w\- /]/g, "").replace(/\s+/g, "-");
    if (!name) return;
    setFolder(name);
    setNewFolder("");
    toast.success(`Uploads will go to “${name}”`);
  };

  return (
    <div data-testid={MEDIA.root}>
      <PageHeader
        title="Media Library"
        subtitle="Upload and organize images, videos, and documents."
        actions={
          <>
            <input
              ref={fileRef}
              type="file"
              multiple
              hidden
              data-testid={MEDIA.fileInput}
              onChange={(e) => {
                upload(Array.from(e.target.files || []));
                e.target.value = "";
              }}
            />
            <input
              ref={replaceRef}
              type="file"
              accept="image/*,video/*,application/pdf"
              hidden
              onChange={(e) => {
                replaceFile(e.target.files?.[0]);
                e.target.value = "";
              }}
            />
            <Button
              type="button"
              variant="ghost"
              disabled={bootstrapping}
              onClick={bootstrapWebsiteAssets}
              className="text-white/80 border border-white/10"
            >
              <Icons.Globe className="w-4 h-4 mr-1.5" />
              {bootstrapping ? "Importing…" : "Import website images"}
            </Button>
            <Button
              data-testid={MEDIA.uploadBtn}
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="btn-gradient text-white border-0"
            >
              <Icons.Upload className="w-4 h-4 mr-1.5" />
              {uploading ? "Uploading…" : "Upload"}
            </Button>
          </>
        }
      />

      {storageInfo?.hint && (
        <GlassCard className="p-3 mb-4 border border-amber-400/20 bg-amber-400/5">
          <div className="flex gap-2 items-start text-sm text-amber-100/90">
            <Icons.AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <div>
              <p className="font-medium text-amber-100">Storage: {storageInfo.backend}</p>
              <p className="text-xs text-amber-100/70 mt-0.5">{storageInfo.hint}</p>
            </div>
          </div>
        </GlassCard>
      )}

      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Icons.Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search files…"
            className="pl-9 bg-black/20 border-white/10 text-white placeholder:text-white/30"
          />
        </div>
        <div className="flex items-center gap-1">
          {["", "image", "video", "document"].map((t) => (
            <button
              key={t || "all"}
              type="button"
              onClick={() => setType(t)}
              className={`h-9 px-3 rounded-lg text-xs border transition-colors capitalize ${
                type === t ? "bg-white/10 border-white/20 text-white" : "border-white/10 text-white/60 hover:text-white"
              }`}
            >
              {t || "All"}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className="text-xs text-white/45">Folder:</span>
        {["all", ...folders].map((value) => (
          <button
            key={value}
            type="button"
            onClick={() => setFolder(value)}
            className={`h-8 px-3 rounded-lg text-xs border ${
              folder === value ? "bg-white/10 border-white/20 text-white" : "border-white/10 text-white/60 hover:text-white"
            }`}
          >
            {value === "all" ? "All files" : value}
          </button>
        ))}
        <div className="flex items-center gap-1.5 ml-1">
          <Input
            value={newFolder}
            onChange={(e) => setNewFolder(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && createFolder()}
            placeholder="New folder name"
            className="h-8 w-36 bg-black/20 border-white/10 text-white text-xs placeholder:text-white/30"
          />
          <Button size="sm" variant="ghost" onClick={createFolder} className="h-8 text-white/70 hover:text-white hover:bg-white/5">
            Create
          </Button>
        </div>
        {selected.length > 0 && (
          <Button size="sm" variant="ghost" onClick={() => setPendingDelete("bulk")} className="ml-auto text-red-200 hover:text-red-100 hover:bg-red-500/10">
            <Icons.Trash2 className="w-4 h-4 mr-1" /> Delete {selected.length} selected
          </Button>
        )}
      </div>

      <GlassCard className="p-3 mb-4">
        <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
          <Label className="text-xs text-white/50 shrink-0">Add by URL</Label>
          <Input
            value={importUrl}
            onChange={(e) => setImportUrl(e.target.value)}
            placeholder="https://…/image.png"
            className="bg-black/20 border-white/10 text-white text-xs h-9"
          />
          <Button type="button" size="sm" onClick={importExternalUrl} className="btn-gradient text-white border-0 h-9 shrink-0">
            Add URL
          </Button>
        </div>
      </GlassCard>

      {uploadQueue.length > 0 && (
        <GlassCard className="p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-white font-medium">Upload progress</span>
            {!uploading && (
              <button type="button" onClick={() => setUploadQueue([])} className="text-xs text-white/50 hover:text-white">
                Clear
              </button>
            )}
          </div>
          <div className="space-y-2">
            {uploadQueue.map((entry) => (
              <div key={entry.id} className="grid grid-cols-[minmax(0,1fr)_52px] gap-3 items-center">
                <div>
                  <div className="flex justify-between text-xs text-white/70 mb-1">
                    <span className="truncate">{entry.name}</span>
                    <span className={entry.status === "failed" ? "text-red-300" : entry.status === "complete" ? "text-emerald-300" : "text-white/50"}>
                      {entry.status === "failed" ? "Failed" : entry.status === "complete" ? "Complete" : `${entry.progress}%`}
                    </span>
                  </div>
                  <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                    <div className={`h-full ${entry.status === "failed" ? "bg-red-400" : "bg-violet-400"}`} style={{ width: `${entry.progress}%` }} />
                  </div>
                </div>
                <Icons.Upload className="w-4 h-4 text-white/35" />
              </div>
            ))}
          </div>
        </GlassCard>
      )}

      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} className="aspect-square rounded-xl bg-white/[0.03] animate-pulse" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <GlassCard className="p-16">
          <div className="flex flex-col items-center gap-3 text-white/50">
            <Icons.Image className="w-10 h-10" />
            <p>No media yet — or old uploads were lost from storage.</p>
            <div className="flex flex-wrap gap-2 justify-center">
              <Button onClick={bootstrapWebsiteAssets} disabled={bootstrapping} className="btn-gradient text-white border-0">
                <Icons.Globe className="w-4 h-4 mr-1.5" /> Import website images
              </Button>
              <Button onClick={() => fileRef.current?.click()} variant="ghost" className="text-white/80 border border-white/10">
                <Icons.Upload className="w-4 h-4 mr-1.5" /> Upload files
              </Button>
            </div>
          </div>
        </GlassCard>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3" data-testid={MEDIA.grid}>
          {items.map((it) => (
            <div key={it.id} className={`glass rounded-xl overflow-hidden group relative ${selected.includes(it.id) ? "ring-2 ring-violet-400" : ""}`}>
              <button
                type="button"
                onClick={() => toggleSelected(it.id)}
                className="absolute z-10 left-2 top-2 w-5 h-5 rounded border border-white/30 bg-black/50 flex items-center justify-center"
                aria-label={`Select ${it.original_filename}`}
              >
                {selected.includes(it.id) && <Icons.Check className="w-3.5 h-3.5 text-white" />}
              </button>
              {it.external_url ? (
                <span className="absolute z-10 right-2 top-2 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-200 border border-emerald-400/20">
                  Linked
                </span>
              ) : null}
              <div className="aspect-square bg-black/40 flex items-center justify-center overflow-hidden">
                <MediaThumb item={it} className="w-full h-full object-cover" />
              </div>
              <div className="p-2.5">
                <div className="text-xs text-white truncate">{it.original_filename}</div>
                <div className="text-[10px] text-white/40 mt-0.5">{bytes(it.size)} · {it.folder || "uploads"}</div>
                {it.alt_text && <div className="text-[10px] text-white/40 mt-1 truncate">Alt: {it.alt_text}</div>}
              </div>
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity duration-200 flex items-center justify-center gap-2 flex-wrap p-2">
                <a href={mediaPublicUrl(it)} target="_blank" rel="noreferrer" className="p-2 rounded-lg glass hover:bg-white/10 text-white" aria-label="Open">
                  <Icons.ExternalLink className="w-4 h-4" />
                </a>
                <button type="button" onClick={() => copyLink(it)} className="p-2 rounded-lg glass hover:bg-white/10 text-white" aria-label="Copy file link">
                  <Icons.Copy className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setReplacingId(it.id);
                    replaceRef.current?.click();
                  }}
                  className="p-2 rounded-lg glass hover:bg-white/10 text-white"
                  aria-label="Replace file"
                  title="Replace file (keeps same id)"
                >
                  <Icons.RefreshCw className="w-4 h-4" />
                </button>
                <button type="button" onClick={() => openMetadata(it)} className="p-2 rounded-lg glass hover:bg-white/10 text-white" aria-label="Edit media details">
                  <Icons.Pencil className="w-4 h-4" />
                </button>
                <button type="button" onClick={() => setPendingDelete(it)} className="p-2 rounded-lg glass hover:bg-red-500/20 text-red-300" aria-label="Delete">
                  <Icons.Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Sheet open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <SheetContent className="w-full sm:max-w-lg glass-strong border-white/10 text-white overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-white">Edit media details</SheetTitle>
            <SheetDescription className="text-white/50">Set accessible text and helpful details for this file.</SheetDescription>
          </SheetHeader>
          <div className="mt-6 space-y-4">
            <div className="aspect-video rounded-lg bg-black/40 overflow-hidden">
              <MediaThumb item={editing || {}} className="w-full h-full object-contain" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-white/50">File name</Label>
              <Input value={metadata.original_filename || ""} onChange={(e) => setMetadata({ ...metadata, original_filename: e.target.value })} className="bg-black/20 border-white/10 text-white" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-white/50">Alt text</Label>
              <Input value={metadata.alt_text || ""} onChange={(e) => setMetadata({ ...metadata, alt_text: e.target.value })} placeholder="Describe the visual content for screen readers" className="bg-black/20 border-white/10 text-white" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-white/50">Caption</Label>
              <Textarea value={metadata.caption || ""} onChange={(e) => setMetadata({ ...metadata, caption: e.target.value })} rows={3} className="bg-black/20 border-white/10 text-white" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-white/50">Tags</Label>
              <Input value={(metadata.tags || []).join(", ")} onChange={(e) => setMetadata({ ...metadata, tags: e.target.value.split(",").map((tag) => tag.trim()).filter(Boolean) })} placeholder="brand, hero, campaign" className="bg-black/20 border-white/10 text-white" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-white/50">Folder</Label>
              <Input value={metadata.folder || ""} onChange={(e) => setMetadata({ ...metadata, folder: e.target.value })} className="bg-black/20 border-white/10 text-white" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-white/50">External / durable URL</Label>
              <Input value={metadata.external_url || ""} onChange={(e) => setMetadata({ ...metadata, external_url: e.target.value })} placeholder="https://… (used when storage file is missing)" className="bg-black/20 border-white/10 text-white" />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                className="text-white/80 border border-white/10"
                onClick={() => {
                  setReplacingId(editing.id);
                  replaceRef.current?.click();
                }}
              >
                <Icons.RefreshCw className="w-4 h-4 mr-1.5" /> Replace file
              </Button>
              <Button onClick={saveMetadata} disabled={savingMetadata} className="btn-gradient text-white border-0">
                <Icons.Save className="w-4 h-4 mr-1.5" /> {savingMetadata ? "Saving…" : "Save details"}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <AlertDialog open={!!pendingDelete} onOpenChange={(open) => !open && setPendingDelete(null)}>
        <AlertDialogContent className="glass-strong border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>{pendingDelete === "bulk" ? `Delete ${selected.length} files?` : "Delete this file?"}</AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">
              {pendingDelete === "bulk"
                ? "Selected files will be removed from the library. Public pages that still point at these URLs will show broken media."
                : `“${pendingDelete?.original_filename || "This file"}” will be removed from the library.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="text-white">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-500 text-white">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
